import React, { useState, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faSearch } from "@fortawesome/pro-regular-svg-icons";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import InputField from "../../components/InputField";
import TableView from "../../components/TableView";
import ConfirmationModal from "../../components/ConfirmationModal";
import Breadcrumbs from "../../components/Breadcrumbs";
import LoadingTablePage from "../LoadingTablePage";
import { useAuth } from "../../providers/AuthContext";
import { getHardwareProducts, deleteHardwareProduct } from "../../api/HardwareProducts";

const COLUMNS = [
  { key: "name", label: "Name" },
  { key: "category", label: "Category" },
  { key: "articleNumber", label: "Article number" },
  { key: "price", label: "Price", render: (item) => (item.price === null || item.price === undefined ? "On request" : `${item.price} ${item.currency}`) },
  { key: "isPublished", label: "Published", render: (item) => (item.isPublished ? "Yes" : "No") },
  { key: "sortOrder", label: "Sort order" },
  { key: "updatedDateTimeUTC", label: "Updated", type: "date" },
];

/**
 * Diflex-wide hardware catalogue (PBI #304). Which customers see which products is configured per
 * agreement on the "Hardware" tab of the agreement page.
 */
const HardwareCataloguePage = () => {
  const { getAccessToken } = useAuth();
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [confirmState, setConfirmState] = useState({ isOpen: false, title: "", confirm: () => {} });

  const loadProducts = useCallback(async () => {
    try {
      const accessToken = await getAccessToken();
      setProducts(await getHardwareProducts(accessToken, true));
    } catch (error) {
      toast.error(error.message || "Failed to load hardware products");
    } finally {
      setLoaded(true);
    }
  }, [getAccessToken]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const handleDelete = async (hardwareProductPublicId) => {
    const toastId = toast.loading("Deleting product...");
    try {
      const accessToken = await getAccessToken();
      await deleteHardwareProduct(hardwareProductPublicId, accessToken);
      await loadProducts();
      toast.success("Product deleted", { id: toastId, iconTheme: { primary: "#4CAF50", secondary: "#DFF2BF" } });
    } catch (error) {
      toast.error(error.message || "Failed to delete product", { id: toastId });
    }
  };

  const confirmDelete = (hardwareProductPublicId) => {
    const product = products.find((p) => p.hardwareProductPublicId === hardwareProductPublicId);
    setConfirmState({
      isOpen: true,
      title: `Delete "${product?.name || "this product"}"? It disappears from every customer's assortment.`,
      confirm: () => handleDelete(hardwareProductPublicId),
    });
  };

  const needle = search.trim().toLowerCase();
  const filteredProducts = needle
    ? products.filter((p) => [p.name, p.category, p.articleNumber].some((v) => (v || "").toLowerCase().includes(needle)))
    : products;

  return (
    <>
      <HeaderBar />
      <div className="flex flex-col">
        <div className="flex flex-row">
          <Sidebar />
          <main className="page">
            <div className="absolute w-full pointer-events-none">
              <LoadingTablePage title={"Hardware catalogue"} loading={!loaded} />
            </div>
            <div className={`${!loaded ? "loading" : ""} page-content`}>
              <Breadcrumbs />
              <div className="flex justify-between items-center">
                <h2 className="text-2xl text-black font-bold pb-2">Hardware catalogue</h2>
                <Link to="/hardware-catalogue/create" className="btn-primary">
                  <FontAwesomeIcon icon={faPlus} className="mr-2 text-lg" />
                  Create product
                </Link>
              </div>
              <div className="rounded-md shadow-md border w-full p-4">
                <div className="flex mb-2 gap-4 justify-end">
                  <InputField
                    iconLeft={false}
                    icon={faSearch}
                    placeholder={"name, category, article number"}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                <div className="overflow-x-auto pb-[250px] mb-[-250px]">
                  <TableView
                    data={filteredProducts}
                    selectedRows={selectedProducts}
                    setSelectedRows={setSelectedProducts}
                    noCheckbox={true}
                    columns={COLUMNS}
                    primaryKey="hardwareProductPublicId"
                    basePath="/hardware-catalogue"
                    handleDelete={confirmDelete}
                  />
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
      <ConfirmationModal
        isOpen={confirmState.isOpen}
        title={confirmState.title}
        onClose={() => setConfirmState((s) => ({ ...s, isOpen: false }))}
        onConfirm={() => {
          setConfirmState((s) => ({ ...s, isOpen: false }));
          confirmState.confirm();
        }}
      />
    </>
  );
};

export default HardwareCataloguePage;
